import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router';
import { Toolbar, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, TablePagination, TableSortLabel, Box, OutlinedInput, InputAdornment, Stack, Skeleton, Typography, Card, CardContent, CardHeader, FormControl, InputLabel, Select, MenuItem, AppBar, Tab, Tabs, Menu, ListItemIcon, ListItemText, FormControlLabel, Backdrop, Dialog, DialogContent } from '@mui/material';
import { Margin, Unsubscribe, Clear, Edit, DepartureBoard, Drafts, AdsClick, Send, ScheduleSend, Watch, Block, MoreVert, DriveFileRenameOutline, Archive, CancelScheduleSend, Delete, Chat, ErrorOutline } from '@mui/icons-material';
import Grid from '@mui/material/Grid';
import { format, differenceInDays } from 'date-fns';
import buildUrl from '../buildUrl';
import DataColumn from '../dataTable/DataColumn';
import useSearch from '../hooks/useSearch';
import useApi from '../hooks/useApi';
import useSnackbar from '../hooks/useSnackbar';
import CenteredProgress from '../loadingOverlay/CenteredProgress';
import LoadingOverlay from '../loadingOverlay/LoadingOverlay';
import BestSubject from './BestSubject';
import CompareValues from './CompareValues';
import UnsubscribeBreakdownCard from './cards/UnsubscribeBreakdownCard';
import BounceBreakdownCard from './cards/BounceBreakdownCard';
import TabbedHeatmapCard from './cards/TabbedHeatmapCard';
import SentimentCard from './cards/SentimentCard';
import ReadTimeByDeviceCard from './cards/ReadTimeByDeviceCard';
import ReadTimeCategoryCard from './cards/ReadTimeCategoryCard';
import EngagementBySubjectContentCard from './cards/EngagementBySubjectContentCard';
import SubjectLengthEngagementChart from './charts/SubjectLengthEngagementChart';
import AverageSendOpenClickRatesChart from './charts/AverageSendOpenClickRatesChart';
import RenderPeriodDateRange from '../account/reportingPeriod/RenderPeriodDateRange';
import { useTheme } from '@mui/material/styles';
import Android12Switch from '../editor/selects/ArchiveToggle';
import Picker from '../picker/Picker';
import RenameItemDialog from './dialogs/RenameItemDialog';
import DeleteItemDialog from './dialogs/DeleteItemDialog';
import useMediaQuery from '@mui/material/useMediaQuery';
import { alpha } from '@mui/system';
import useSuspenseFetch from '../hooks/useSuspenseFetch';
import TypedValue from '../TypedValue';

const rowsPerPageOptions = [10, 25, 50];

const columns = [
    { name: 'name', label: 'Name', orderDesc: false, dataType: 'text', minWidth: 300 },
    { name: 'subject', label: 'Subject', orderDesc: false, dataType: 'text', minWidth: 400 },
    { name: 'status', label: 'Status', orderDesc: false, dataType: 'status', minWidth: 120 },
    { name: 'type', label: 'Type', orderDesc: false, dataType: 'text' },
    { name: 'sendDateTime', label: 'Date/Time', align: 'right', orderDesc: false, dataType: 'dateTime', nowrap: true },
    { name: 'selected', label: 'Selected', align: 'right', orderDesc: true, dataType: 'number' },
    { name: 'sent', label: 'Sent', align: 'right', orderDesc: true, dataType: 'number' },
    { name: 'uniqueOpens', label: 'Unique Opens', align: 'right', orderDesc: true, dataType: 'number', nowrap: true },
    { name: 'uniqueClicks', label: 'Unique Clicks', align: 'right', orderDesc: true, dataType: 'number', nowrap: true },
    { name: 'numberOfLinks', label: 'Links', align: 'right', orderDesc: true, dataType: 'number' }
];

const LoadingFeedbackDialog = ({ selectedPeriods }) => {
    const lengthInDays = period => {
        const start = new Date(period[0]);
        const end = new Date(period[1]);

        return differenceInDays(end, start);
    };

    const periodA = selectedPeriods[0];
    const periodB = selectedPeriods.length > 1 ? selectedPeriods[1] : null;

    const aDays = lengthInDays(periodA);

    let bDays = 0;

    if (periodB) {
        bDays = lengthInDays(periodB);
    }

    const totalDays = aDays + bDays;

    return (
        <Dialog
            open={true}
            maxWidth="xs"
        >
            <DialogContent>
                <Stack spacing={2} sx={{ py: 1 }}>
                    <CenteredProgress colour="primary" />
                    <Typography
                        sx={{ textAlign: 'center' }}
                        variant="body2"
                    >
                        Loading {<TypedValue value={totalDays} type="number" accuracy={0} />} days of data
                    </Typography>
                </Stack>
            </DialogContent>
        </Dialog>
    );
};

const ReportMenu = ({ report, onRename, onArchive, onUnArchive, onDelete, status, isBeingUsed = false }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const archiveLabel = report.archived ? 'Unarchive' : 'Archive';

    const handleClick = e => {
        e.preventDefault();
        e.stopPropagation();

        setAnchorEl(e.currentTarget);
    };

    const handleClose = e => {
        e.preventDefault();
        e.stopPropagation();

        setAnchorEl(null);
    };

    const handleAction = (e, action) => {
        e.stopPropagation();

        action();
        setAnchorEl(null);
    };

    return (
        <>
            <IconButton onClick={handleClick} size="small" disabled={isBeingUsed}>
                <MoreVert fontSize="small" />
            </IconButton>
            <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                <MenuItem onClick={e => handleAction(e, onRename)}>
                    <ListItemIcon>
                        <DriveFileRenameOutline fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Rename</ListItemText>
                </MenuItem>
                <MenuItem onClick={e => {
                    if (archiveLabel === 'Archive') {
                        handleAction(e, onArchive);
                    }
                    else {
                        handleAction(e, onUnArchive);
                    }
                }}>
                    <ListItemIcon>
                        <Archive fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{archiveLabel}</ListItemText>
                </MenuItem>
                {status === 'Scheduled' ? (
                    <MenuItem onClick={e => handleAction(e, onDelete)}>
                        <ListItemIcon>
                            <CancelScheduleSend fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Cancel & Revert to Draft</ListItemText>
                    </MenuItem>
                ) : (
                    <MenuItem onClick={e => handleAction(e, onDelete)}>
                        <ListItemIcon>
                            <Delete fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Delete & Revert to Draft</ListItemText>
                    </MenuItem>
                )}
            </Menu>
        </>
    );
};

const Emails = ({ selectedPeriods, prevSelectedPeriods, compare, periodColours, topOffset, filterTopics, prevFilterTopics, onChangeTopicsFilter }) => {
    const { data: topics } = useSuspenseFetch('topics');
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const { showSnackbar } = useSnackbar();
    const routeParams = useParams();
    const { handleGet, handlePut, handleDelete } = useApi();
    const params = new URLSearchParams(location.search);
    const [reports, setReports] = useState(null);
    const [count, setCount] = useState(0);
    const [searchValue, setSearchValue] = useState(params.get('searchValue') || '');
    const [tempSearchValue, setTempSearchValue] = useSearch(setSearchValue, 500, searchValue);
    const [rowsPerPage, setRowsPerPage] = useState(params.get('rowsPerPage') ?? rowsPerPageOptions[0]);
    const [page, setPage] = useState(0);
    const [summaryA, setSummaryA] = useState(null);
    const [summaryB, setSummaryB] = useState(null);
    const [orderBy, setOrderBy] = useState('sendDateTime');
    const [orderDesc, setOrderDesc] = useState(true);
    const [isInitialising, setIsInitialising] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingB, setIsLoadingB] = useState(false);
    const [filterByPeriod, setFilterByPeriod] = useState(params.get('filterByPeriod') ?? 'a');
    const [tab, setTab] = useState('');
    const [archived, setArchived] = useState(params.get('archived') === 'true');
    const [itemToRename, setItemToRename] = useState(null);
    const [itemToDelete, setItemToDelete] = useState(null);

    const showB = Boolean(compare && summaryB);

    const noEmails = showB ? !Boolean(summaryA?.emails || summaryB?.emails) : !Boolean(summaryA?.emails);

    const heightMatchedContainer = useMediaQuery(theme.breakpoints.down('lg'))
        ? undefined
        : {
            height: '100%',
            pb: 0,
            '& > :last-child': {
                pb: 0
            }
        };

    const handleFetchCount = async () => {
        const filterByPeriodDates = filterByPeriod !== 'none' ? filterByPeriod === 'a' ? selectedPeriods[0] : selectedPeriods[1] : null;

        const from = filterByPeriodDates !== null ? format(filterByPeriodDates[0], 'yyyy-MM-dd') : null;
        const to = filterByPeriodDates !== null ? format(filterByPeriodDates[1], 'yyyy-MM-dd') : null;

        const topicIds = filterTopics?.map(e => e.id);

        const params = {
            search: searchValue,
            archived,
            ...(from && { fromDate: from }),
            ...(to && { toDate: to }),
            ...(filterTopics && { topicIds })
        };

        const url = buildUrl('reports/count', params);
        const response = await handleGet(url);

        if (!response.ok) {
            return;
        }

        const data = await response.json();
        setCount(data);
    };

    const handleFetchReports = async () => {
        setIsLoading(true);

        const filterByPeriodDates = filterByPeriod !== 'none' ? filterByPeriod === 'a' ? selectedPeriods[0] : selectedPeriods[1] : null;

        const from = filterByPeriodDates !== null ? format(filterByPeriodDates[0], 'yyyy-MM-dd') : null;
        const to = filterByPeriodDates !== null ? format(filterByPeriodDates[1], 'yyyy-MM-dd') : null;

        const topicIds = filterTopics?.map(e => e.id);

        const params = {
            search: searchValue,
            skip: page * rowsPerPage,
            take: rowsPerPage,
            orderBy,
            isDescending: orderDesc,
            archived,
            ...(from && { fromDate: from }),
            ...(to && { toDate: to }),
            ...(filterTopics && { topicIds })
        };

        const url = buildUrl('reports', params);
        const response = await handleGet(url);

        if (!response.ok) {
            return;
        }

        const data = await response.json();
        setReports(data);
        setIsLoading(false);
    };

    const handleFetchSummary = async (period, isB = false) => {
        isB ? setIsLoadingB(true) : setIsLoading(true);

        const fromDate = period[0];
        const toDate = period[1];

        const topics = filterTopics?.map(e => e.id);

        const params = {
            ...(fromDate && { fromDate: format(fromDate, 'yyyy-MM-dd') }),
            ...(toDate && { toDate: format(toDate, 'yyyy-MM-dd') }),
            ...(filterTopics?.length > 0 && { topics })
        };

        const url = buildUrl('reports/emails/summary', params);
        const response = await handleGet(url);

        if (!response.ok) {
            return;
        }

        const data = await response.json();

        isB ? setSummaryB(data) : setSummaryA(data);

        if (!isInitialising) {
            isB ? setIsLoadingB(false) : setIsLoading(false);
        }
    };

    const handleSort = (field, defaultOrder) => {
        if (field === orderBy) {
            setOrderDesc(!orderDesc);
        }
        else {
            setOrderBy(field);
            setOrderDesc(defaultOrder);
        }
    };

    const handleToggleArchive = async ({ item, archived }) => {
        setIsLoading(true);

        const response = await handlePut(`reports/${item.id}/archive`, { archived });

        if (!response.ok) {
            showSnackbar('Archive Report failed', 'error');
        }
        else archived ? showSnackbar('Report archived', 'success') : showSnackbar('Report unarchived', 'succcess');

        setIsLoading(false);
        handleFetchCount();
        handleFetchReports();

    };

    const handleDeleteItem = async () => {
        const response = await handleDelete(`reports/${itemToDelete.id}`);

        if (itemToDelete.id?.toString() === routeParams.id?.toString()) {
            navigate('/newreports');
        }

        if (!response.ok) {
            showSnackbar('Delete Report failed', 'error');
        }
        else {
            showSnackbar('Report deleted', 'success');
        }

        setItemToDelete(null);
        handleFetchReports();
    };

    const handleRename = async name => {
        const response = await handlePut(`reports/${itemToRename.id}/rename`, { name });

        if (!response.ok) {
            showSnackbar('Rename Report failed', 'error');
        }
        else {
            showSnackbar('Report renamed', 'success');
        }

        setItemToRename(null);
        handleFetchReports();
    };

    const handleUpdateTopics = async ({ report, topics = null }) => {
        const topicIds = topics?.map(e => e.id);

        try {
            const response = await handlePut(`reports/${report.id}/topics`, { topicIds });

            if (!response.ok) {
                const errorMessage = response.status === 404
                    ? 'Email not found'
                    : 'Failed to update Email Topics';

                showSnackbar(errorMessage, 'error');
            }
        }
        catch (error) {
            console.error('Error updating Email Topics:', error);
            showSnackbar('An error occurred while updating Email Topics.', 'error');
        }
    };

    const handleChangeTab = tab => {
        navigate(tab ? `/newreports/emails/${tab}` : '/newreports/emails');
    };

    const handleOpenReport = id => {
        navigate(`/newreports/emails/${id}`);
    };

    useEffect(() => {
        if (selectedPeriods.length === 0 || tab !== 'insights') {
            return;
        }

        // only fetch data if null or period has changed

        if (!summaryA || (selectedPeriods[0] !== prevSelectedPeriods[0]) || filterTopics !== prevFilterTopics) {
            handleFetchSummary(selectedPeriods[0]);
        }

        if (compare && selectedPeriods.length > 1 && (!summaryB || selectedPeriods[1] !== prevSelectedPeriods[1] || filterTopics !== prevFilterTopics)) {
            handleFetchSummary(selectedPeriods[1], true);
        }
    }, [selectedPeriods, tab, filterTopics]);

    useEffect(() => {
        const tab = location.pathname.split('/')[3] || '';

        setTab(tab);
    }, [location]);

    useEffect(() => {
        reports && setIsInitialising(false);
    }, [reports]);

    useEffect(() => {
        if (tab === 'dashboard') {
            return;
        }

        handleFetchCount();
    }, [searchValue, filterByPeriod, filterTopics, archived]);

    useEffect(() => {
        if (tab === 'dashboard') {
            return;
        }

        setPage(0);
    }, [searchValue, orderBy, orderDesc, rowsPerPage, filterByPeriod, filterTopics, archived]);

    useEffect(() => {
        if (tab === 'dashboard') {
            return;
        }

        handleFetchReports();
    }, [searchValue, orderBy, orderDesc, rowsPerPage, page, filterByPeriod, filterTopics, archived]);

    useEffect(() => {
        const params = new URLSearchParams();

        if (searchValue) params.set('searchValue', searchValue);
        if (filterByPeriod) params.set('filterByPeriod', filterByPeriod);
        if (archived) params.set('archived', archived);
        if (rowsPerPage) params.set('rowsPerPage', rowsPerPage);
        params.set('tab', tab);

        navigate(`${location.pathname}?${params.toString()}`);
    }, [searchValue, filterByPeriod, archived, rowsPerPage, tab]);

    useEffect(() => {
        if (filterByPeriod !== 'none') {
            if (filterByPeriod === 'a' && (selectedPeriods[0] !== prevSelectedPeriods[0]) || filterByPeriod === 'b' && (selectedPeriods[1] !== prevSelectedPeriods[1])) {
                setIsLoading(true);
                setPage(0);
                handleFetchCount();
                handleFetchReports();
            }
        }
    }, [selectedPeriods]);

    if (isInitialising) {
        return <LoadingOverlay />;
    }

    return (
        <>
            <AppBar
                position="sticky"
                color="inherit"
                sx={{
                    top: topOffset,
                    flexShrink: 0, // fixes a bug causing height to shrink on scroll
                    zIndex: theme.zIndex.appBar - 1,
                    borderTop: 1,
                    borderColor: theme.palette.divider
                }}
            >
                <Tabs value={tab} onChange={(e, value) => handleChangeTab(value)}>
                    <Tab label="Find a Report" value="" />
                    <Tab label="Insights" value="insights" />
                </Tabs>
            </AppBar>
            <Box p={2}>
                {tab === 'insights' ? summaryA ? (
                    <>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, lg: 3 }}>
                                <CompareValues
                                    a={summaryA.sumOfTotalOpens}
                                    b={summaryB?.sumOfTotalOpens}
                                    showB={showB}
                                    text="Total Opens"
                                    valueType="number"
                                    icon={<Drafts fontSize="small" />}
                                    periodColours={periodColours}
                                    selectedPeriods={selectedPeriods}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, lg: 3 }}>
                                <CompareValues
                                    a={summaryA.sumOfTotalClicks}
                                    b={summaryB?.sumOfTotalClicks}
                                    showB={showB}
                                    text="Total Clicks"
                                    valueType="number"
                                    icon={<AdsClick fontSize="small" />}
                                    periodColours={periodColours}
                                    selectedPeriods={selectedPeriods}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, lg: 3 }}>
                                <CompareValues
                                    a={summaryA.sumOfUniqueOpens}
                                    b={summaryB?.sumOfUniqueOpens}
                                    showB={showB}
                                    text="Unique Opens"
                                    valueType="number"
                                    icon={<Drafts fontSize="small" />}
                                    periodColours={periodColours}
                                    selectedPeriods={selectedPeriods}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, lg: 3 }}>
                                <CompareValues
                                    a={summaryA.sumOfUniqueClicks}
                                    b={summaryB?.sumOfUniqueClicks}
                                    showB={showB}
                                    text="Unique Clicks"
                                    valueType="number"
                                    icon={<AdsClick fontSize="small" />}
                                    periodColours={periodColours}
                                    selectedPeriods={selectedPeriods}
                                />
                            </Grid>
                            <Grid container size={12}>
                                <Grid container size={{ xs: 12, lg: 8 }}>
                                    <Grid size={{ xs: 12, lg: 4 }}>
                                        <ReadTimeCategoryCard
                                            a={summaryA}
                                            b={showB ? summaryB : null}
                                            aColour={periodColours[0]}
                                            bColour={periodColours[1]}
                                            selectedPeriods={selectedPeriods}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, lg: 8 }}>
                                        <ReadTimeByDeviceCard
                                            a={summaryA}
                                            b={showB ? summaryB : null}
                                            aColour={periodColours[0]}
                                            bColour={periodColours[1]}
                                            selectedPeriods={selectedPeriods}
                                        />
                                    </Grid>
                                    <Grid size={12}>
                                        <TabbedHeatmapCard
                                            a={summaryA}
                                            b={summaryB}
                                            showB={showB}
                                        />
                                    </Grid>
                                </Grid>
                                <Grid
                                    container
                                    sx={heightMatchedContainer}
                                    size={{ xs: 12, lg: 4 }}
                                >
                                    <Grid size={12}>
                                        <CompareValues
                                            a={summaryA.totalEmailsCreated}
                                            b={summaryB?.totalEmailsCreated}
                                            showB={showB}
                                            text="Emails Created"
                                            valueType="number"
                                            icon={<Edit fontSize="small" />}
                                            periodColours={periodColours}
                                            selectedPeriods={selectedPeriods}
                                        />
                                    </Grid>
                                    <Grid size={12}>
                                        <SentimentCard
                                            a={summaryA}
                                            b={showB ? summaryB : null}
                                            aColour={periodColours[0]}
                                            bColour={periodColours[1]}
                                            selectedPeriods={selectedPeriods}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid size={{ xs: 12, lg: 3 }}>
                                <CompareValues
                                    a={summaryA.bestSendTimeForClicks}
                                    b={summaryB?.bestSendTimeForClicks}
                                    showB={showB}
                                    text="Best Send Time for Clicks"
                                    valueType="hour"
                                    icon={<Watch fontSize="small" />}
                                    periodColours={periodColours}
                                    noDataA={!summaryA.emails}
                                    noDataB={!summaryB?.emails}
                                    selectedPeriods={selectedPeriods}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, lg: 3 }}>
                                <CompareValues
                                    a={summaryA.bestSendTimeForOpens}
                                    b={summaryB?.bestSendTimeForOpens}
                                    showB={showB}
                                    text="Best Send Time for Opens"
                                    valueType="hour"
                                    icon={<DepartureBoard fontSize="small" />}
                                    periodColours={periodColours}
                                    noDataA={!summaryA.emails}
                                    noDataB={!summaryB?.emails}
                                    selectedPeriods={selectedPeriods}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, lg: 3 }}>
                                <CompareValues
                                    a={summaryA.mostFrequentSendTime}
                                    b={summaryB?.mostFrequentSendTime}
                                    showB={showB}
                                    text="Most Frequent Send Time"
                                    valueType="hour"
                                    icon={<ScheduleSend fontSize="small" />}
                                    periodColours={periodColours}
                                    noData={noEmails}
                                    noDataA={!summaryA.emails}
                                    noDataB={!summaryB?.emails}
                                    selectedPeriods={selectedPeriods}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, lg: 3 }}>
                                <CompareValues
                                    a={summaryA.averageSendFrequency}
                                    b={summaryB?.averageSendFrequency}
                                    showB={showB}
                                    text="Average Weekly Send Frequency"
                                    valueType="number"
                                    icon={<Send fontSize="small" />}
                                    periodColours={periodColours}
                                    selectedPeriods={selectedPeriods}
                                    accuracy={2}
                                    explanation={(
                                        <>
                                            <Typography variant="body1" fontWeight={500} sx={{ pb: 0.5 }}>Average Send Frequency</Typography>
                                            <Typography variant="body2" color={theme.palette.text.secondary}>Total campaigns sent divided by the number of weeks in the selected time period</Typography>
                                        </>
                                    )}
                                />
                            </Grid>
                            <Grid container size={12}>
                                <Grid size={{ xs: 12, lg: 8 }}>
                                    <Card sx={{ m: 0, p: 0 }}>
                                        <CardHeader
                                            sx={{ m: 0, py: 1 }}
                                            title="Average Send Size, Open and Click Rates"
                                            slotProps={{
                                                title: {
                                                    variant: 'h6',
                                                    fontWeight: 'normal'
                                                }
                                            }}
                                            avatar={<Margin />}
                                        />
                                        <CardContent
                                            sx={{
                                                m: 0,
                                                p: 0,
                                                '&:last-child': {
                                                    paddingBottom: 0
                                                }
                                            }}
                                        >
                                            <AverageSendOpenClickRatesChart
                                                a={summaryA}
                                                b={showB ? summaryB : null}
                                                periodColours={periodColours}
                                            />
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid size={{ xs: 12, lg: 4 }} container sx={heightMatchedContainer}>
                                    <Grid size={12}>
                                        <UnsubscribeBreakdownCard
                                            a={summaryA}
                                            b={summaryB}
                                            showB={showB}
                                            text="Unsubscribe Rate"
                                            icon={<Unsubscribe fontSize="small" />}
                                            periodColours={periodColours}
                                            selectedPeriods={selectedPeriods}
                                        />
                                    </Grid>
                                    <Grid size={12}>
                                        <BounceBreakdownCard
                                            a={summaryA}
                                            b={summaryB}
                                            showB={showB}
                                            text="Bounce Rate"
                                            icon={<Block fontSize="small" />}
                                            periodColours={periodColours}
                                            selectedPeriods={selectedPeriods}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid size={{ xs: 12, lg: 6 }}>
                                <Card sx={{ height: '100%', m: 0, p: 0 }}>
                                    <CardHeader
                                        sx={{ m: 0, py: 1 }}
                                        title="Engagement by Subject Line Length"
                                        slotProps={{
                                            title: {
                                                variant: 'h6',
                                                fontWeight: 'normal'
                                            }
                                        }}
                                        avatar={<Chat />}
                                    />
                                    <CardContent
                                        sx={{
                                            m: 0,
                                            p: 0,
                                            '&:last-child': {
                                                paddingBottom: 0
                                            }
                                        }}
                                    >
                                        <SubjectLengthEngagementChart
                                            a={summaryA}
                                            b={showB ? summaryB : null}
                                            periodColours={periodColours}
                                        />
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid container size={{ xs: 12, lg: 6 }}>
                                <Grid size={12}>
                                    <BestSubject
                                        a={summaryA.bestSubjectLineLengthByOpenRate}
                                        b={summaryB?.bestSubjectLineLengthByOpenRate}
                                        showB={showB}
                                        periodColours={periodColours}
                                        selectedPeriods={selectedPeriods}
                                    />
                                </Grid>
                                <Grid size={12}>
                                    <EngagementBySubjectContentCard
                                        a={summaryA}
                                        b={showB ? summaryB : null}
                                        aColour={periodColours[0]}
                                        bColour={periodColours[1]}
                                        selectedPeriods={selectedPeriods}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Backdrop
                            open={noEmails}
                            sx={{
                                backgroundColor: alpha(theme.palette.background.paper, 0.85)
                            }}
                        >
                            <Stack spacing={2} sx={{ alignItems: 'center' }}>
                                <ErrorOutline color="primary" sx={{ fontSize: 80 }} />
                                <Typography variant="h5" sx={{ textAlign: 'center' }}>
                                    There were no emails sent in this period. Try adjusting your filters
                                </Typography>
                            </Stack>
                        </Backdrop>
                        {(!isInitialising && (isLoading || isLoadingB)) && (
                            <LoadingFeedbackDialog
                                selectedPeriods={selectedPeriods}
                            />
                        )}
                    </>
                ) : (
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, lg: 3 }}>
                            <Skeleton height={110} />
                        </Grid>
                        <Grid size={{ xs: 12, lg: 3 }}>
                            <Skeleton height={110} />
                        </Grid>
                        <Grid size={{ xs: 12, lg: 3 }}>
                            <Skeleton height={110} />
                        </Grid>
                        <Grid size={{ xs: 12, lg: 3 }}>
                            <Skeleton height={110} />
                        </Grid>
                        <Grid size={{ xs: 12, lg: 2 }}>
                            <Skeleton height={300} />
                        </Grid>
                        <Grid size={{ xs: 12, lg: 6 }}>
                            <Skeleton height={300} />
                        </Grid>
                        <Grid size={{ xs: 12, lg: 4 }}>
                            <Skeleton height={300} />
                        </Grid>
                        <Grid size={{ xs: 12, lg: 3 }}>
                            <Skeleton height={110} />
                        </Grid>
                        <Grid size={{ xs: 12, lg: 3 }}>
                            <Skeleton height={110} />
                        </Grid>
                        <Grid size={{ xs: 12, lg: 3 }}>
                            <Skeleton height={110} />
                        </Grid>
                        <Grid size={{ xs: 12, lg: 3 }}>
                            <Skeleton height={110} />
                        </Grid>
                    </Grid>
                ) : (
                    <Card sx={{ m: 0, p: 0 }}>
                        <CardContent
                            sx={{
                                m: 0,
                                p: 0,
                                '&:last-child': {
                                    paddingBottom: 0
                                }
                            }}
                        >
                            <Toolbar disableGutters sx={{ mx: 1 }}>
                                <OutlinedInput
                                    value={tempSearchValue}
                                    onChange={e => setTempSearchValue(e.target.value)}
                                    placeholder="Search..."
                                    size="small"
                                    sx={{ m: 1 }}
                                    endAdornment={(
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setTempSearchValue('')}
                                                edge="end"
                                                disabled={!Boolean(tempSearchValue)}
                                            >
                                                <Clear />
                                            </IconButton>
                                        </InputAdornment>
                                    )}
                                />
                                <FormControl margin="none">
                                    <InputLabel id="select-period-dates-label">Filter by Period</InputLabel>
                                    <Select
                                        labelId="select-period-dates-label"
                                        id="select-period-dates"
                                        value={filterByPeriod}
                                        label="Filter by Period"
                                        onChange={e => setFilterByPeriod(e.target.value)}
                                        size="small"
                                        sx={{ minWidth: 120 }}
                                    >
                                        <MenuItem value="none">None</MenuItem>
                                        {selectedPeriods.length > 0 && (
                                            <MenuItem value="a">
                                                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mr: 1, display: 'inline' }}>
                                                    Period A:
                                                </Typography>
                                                <RenderPeriodDateRange
                                                    period={selectedPeriods[0]}
                                                />
                                            </MenuItem>
                                        )}
                                        {(selectedPeriods.length > 1 && compare) && (
                                            <MenuItem value="b">
                                                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mr: 1, display: 'inline' }}>
                                                    Period B:
                                                </Typography>
                                                <RenderPeriodDateRange
                                                    period={selectedPeriods[1]}
                                                />
                                            </MenuItem>
                                        )}
                                    </Select>
                                </FormControl>
                                <FormControlLabel
                                    sx={{ ml: 1 }}
                                    control={(
                                        <Android12Switch
                                            checked={archived}
                                            onChange={e => setArchived(e.target.checked)}
                                        />
                                    )}
                                    label="Archived"
                                />
                            </Toolbar>
                            <Divider />
                            {isLoading ? (
                                <Box py={4}>
                                    <CenteredProgress colour="primary" />
                                </Box>
                            ) : (
                                <>
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    {columns.map(col => (
                                                        <TableCell
                                                            key={col.name}
                                                            component="th"
                                                            scope="row"
                                                            align={col.align}
                                                            sx={{
                                                                whiteSpace: col.nowrap === true ? 'nowrap' : undefined,
                                                                minWidth: col.minWidth
                                                            }}
                                                        >
                                                            <TableSortLabel
                                                                active={orderBy === col.name}
                                                                direction={orderDesc ? 'desc' : 'asc'}
                                                                onClick={() => handleSort(col.name, col.orderDesc)}
                                                            >
                                                                {col.label}
                                                            </TableSortLabel>
                                                        </TableCell>
                                                    ))}
                                                    <TableCell scope="row" align="left" sx={reports?.length > 0 ? { minWidth: 500 } : undefined}>Topics</TableCell>
                                                    <TableCell />
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {reports?.length > 0 ? reports.map(report => (
                                                    <TableRow
                                                        key={report.id}
                                                        hover={true}
                                                    >
                                                        {columns.map(col => (
                                                            <DataColumn
                                                                key={col.name}
                                                                align={col.align}
                                                                dataType={col.dataType}
                                                                value={report[col.name]}
                                                                nowrap={col.nowrap}
                                                                cursor={col.name === 'name' ? 'pointer' : 'default'}
                                                                onClick={() => col.name === 'name' && handleOpenReport(report.id)}
                                                                isLoading={col.name === 'sent' && report.status === 'Sending'}
                                                            />
                                                        ))}
                                                        <TableCell align="left" sx={{ p: 0 }}>
                                                            <Picker
                                                                availableItems={topics}
                                                                selectedItems={report.topics}
                                                                onChangeItems={topics => handleUpdateTopics({ report, topics })}
                                                                onClickItem={topic => onChangeTopicsFilter(topic)}
                                                            />
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <ReportMenu
                                                                report={report}
                                                                status={report.status}
                                                                onRename={() => setItemToRename(report)}
                                                                onArchive={() => handleToggleArchive({ item: report, archived: true })}
                                                                onUnArchive={() => handleToggleArchive({ item: report, archived: false })}
                                                                onDelete={() => setItemToDelete(report)}
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                )) : (
                                                    <TableRow>
                                                        <TableCell colSpan="12" align="center" sx={{ p: 5 }}>
                                                            No data
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    <TablePagination
                                        rowsPerPageOptions={rowsPerPageOptions}
                                        component="div"
                                        count={count}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        onPageChange={(e, newPage) => setPage(newPage)}
                                        onRowsPerPageChange={e => setRowsPerPage(e.target.value)}
                                    />
                                </>
                            )}
                        </CardContent>
                    </Card>
                )}
            </Box>
            {itemToRename && (
                <RenameItemDialog
                    item={itemToRename}
                    onClose={() => setItemToRename(null)}
                    onSubmit={handleRename}
                />
            )}
            {itemToDelete && (
                <DeleteItemDialog
                    item={itemToDelete}
                    onClose={() => setItemToDelete(null)}
                    onSubmit={handleDeleteItem}
                />
            )}
        </>
    );
};

export default Emails;